/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Wallet, Settings2, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/lib/divine";

const REASONS = [
  {
    icon: CheckCircle2,
    title: "14+ Years of Excellence",
    body: "Established in 2012, our team brings over a decade of hands-on experience, meticulous execution, and localised expertise to Valsad and beyond.",
  },
  {
    icon: Sparkles,
    title: "Vastu-Integrated Interiors",
    body: "Expert design planning aligned with traditional Vastu elements to enhance the well-being and positive energy of every environment.",
  },
  {
    icon: Wallet,
    title: "Affordable Luxury",
    body: "Breathtaking, custom-made interiors shouldn't break the bank. We balance premium aesthetics with cost-conscious planning.",
  },
  {
    icon: Settings2,
    title: "End-to-End Turnkey",
    body: "From 3D design concepts and layout planning to custom furniture procurement and final site handoff — everything under one roof.",
  },
];

const SERVICES_LIST = [
  {
    title: "Residential Makeovers",
    body: "Modular kitchens, luxury living rooms, serene bedrooms, and custom mandir / puja spaces.",
  },
  {
    title: "Commercial & Retail",
    body: "Ergonomic corporate offices, optimised retail layouts, and high-impact boutique & showroom environments.",
  },
  {
    title: "Vastu Shastra Consultation",
    body: "Comprehensive evaluation and adjustment strategies for existing properties or fresh construction.",
  },
  {
    title: "3D Visualisation",
    body: "High-fidelity conceptual renderings — walk through your space before construction even begins.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen">
      {/* HERO */}
      <section className="pt-16 pb-12 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6">About the Studio</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Where <span className="italic text-[#D4A437]">design</span> meets <span className="italic text-[#D4A437]">dharma</span>.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#6B6B6B] leading-relaxed">
              Welcome to Divine Interiors — where we believe every space we touch should feel nothing short of divine. Founded in 2012 in Valsad, Gujarat by Ratnesh Shah, we transform ordinary houses, offices and commercial properties into soulful, highly functional environments.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src="https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
              alt="Divine Interiors studio space"
              className="w-full h-[60vh] object-cover"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7 space-y-6 lg:pl-4">
            <div className="overline mb-2">Our Philosophy</div>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.1]">
              Design blended with <span className="italic text-[#D4A437]">harmony</span>.
            </h2>
            <p className="text-[#0A0A0A]/85 leading-relaxed font-serif text-lg">
              What truly sets Divine Interiors apart is our ability to bridge contemporary aesthetics with ancient wisdom.
            </p>
            <p className="text-[#6B6B6B] leading-relaxed">
              We don't just arrange furniture or pick palettes — we optimise the energy flow of your space. By seamlessly integrating Vastu Shastra principles into our modern space planning, your interiors bring peace, prosperity and balance, with style fully intact.
            </p>
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-black/10 mt-8">
              <div>
                <div className="font-serif text-4xl text-[#D4A437]">14+</div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#6B6B6B] mt-2">Years</div>
              </div>
              <div>
                <div className="font-serif text-4xl text-[#D4A437]">120+</div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#6B6B6B] mt-2">Spaces</div>
              </div>
              <div>
                <div className="font-serif text-4xl text-[#D4A437]">3</div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#6B6B6B] mt-2">Awards</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 md:py-32 border-t border-black/5 bg-[#FAF7F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="overline mb-4">Why Choose Us</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              What makes us <span className="italic text-[#D4A437]">divine</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {REASONS.map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.title} className="border border-black/10 bg-white p-8 md:p-10 hover:border-[#D4A437]/50 transition-colors">
                  <Icon className="w-8 h-8 text-[#D4A437] mb-6" strokeWidth={1.3} />
                  <h3 className="font-serif text-2xl md:text-3xl">{r.title}</h3>
                  <p className="text-[#6B6B6B] mt-4 leading-relaxed">{r.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER QUOTE */}
      <section className="py-24 md:py-32 border-t border-black/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-8">Meet the Founder</div>
          <p className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#0A0A0A]">
            "Every space we create must tell a story, serve a purpose, and feel <span className="italic text-[#D4A437]">divine</span>."
          </p>
          <div className="mt-10 text-sm tracking-widest uppercase text-[#0A0A0A]">
            — Ratnesh Shah
          </div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6B6B6B] mt-2">
            Founder & Principal Consultant
          </div>
          <p className="text-[#6B6B6B] leading-relaxed max-w-2xl mx-auto mt-10">
            With a strong foundation in design architecture and an intuitive understanding of spatial energy, Ratnesh established Divine Interiors with a singular mission: to make timeless, personalised, and sustainable design accessible. His detail-oriented execution and trend-driven yet classic style have earned the firm widespread recognition across Gujarat, including prestigious design accolades.
          </p>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="py-24 md:py-32 border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="overline mb-4">Our Core Services</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Everything, <span className="italic text-[#D4A437]">under one roof</span>.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {SERVICES_LIST.map((s, idx) => (
              <div key={s.title} className="bg-white p-8 md:p-10">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A437] mb-3">{String(idx + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-2xl md:text-3xl">{s.title}</h3>
                <p className="text-[#6B6B6B] mt-4 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-black/5 bg-[#FAF7F0] mandala-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-6">Located in Valsad, Gujarat</div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight">
            Let's create something <span className="italic text-[#D4A437]">divine</span> together.
          </h2>
          <p className="text-[#6B6B6B] mt-6 max-w-2xl mx-auto">
            Remodelling a single room or planning a massive commercial build — let our team bring your dream to life with creativity, balance, and care.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/booking" data-testid="about-book" className="btn-gold">
              Book Consultation <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a href={`tel:${BRAND.phoneRaw}`} data-testid="about-call" className="btn-ghost">
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
